/**
 * Worker protocol — the contract between the React main thread and the engine Web Worker.
 *
 * The CPU-heavy engine (candidate scans, rising-sign breakdown, holdout validation, drilldowns)
 * runs in a dedicated module worker so a multi-second sweep never freezes the UI. The engine
 * functions mutate `user_profile`/`run_data` in place AND read/write the synchronous storage
 * layer (json_store over a backend). To keep json_store semantics identical on both sides, each
 * request ships a SNAPSHOT of the relevant storage keys; the worker seeds a MemoryBackend with
 * it, runs the op, and ships back the full resulting snapshot so the main thread can persist the
 * delta to localStorage.
 *
 * OpenAI-touching ops (fact extraction, AI review, trait chat, photo traits) do NOT run here —
 * they are network-bound and stay on the main thread so the BYOK key never enters the worker.
 *
 * The snapshot/delta helpers below are pure and unit-tested (worker_protocol.test.ts); the
 * worker and client are thin wrappers around them.
 */

/** A serialized copy of storage: logical path → file content (JSON strings). */
export type StorageSnapshot = Record<string, string>;

/** Engine operations the worker can run. All are synchronous, deterministic, CPU-bound. */
export type WorkerOp =
  | "run_internal_matching"
  | "run_iteration"
  | "regenerate_internal_candidates"
  | "rising_sign_breakdown"
  | "dasha_era_segments"
  | "rising_sign_confidence"
  | "holdout_validation"
  | "holdout_validation_per_sign"
  | "psychology_invariants"
  | "chart_overview"
  | "candidate_audit"
  | "compare_marked_times";

export interface WorkerRequest {
  /** Correlates response to request. */
  id: number;
  op: WorkerOp;
  /** The active user's profile (mutated in place by the op; echoed back updated). */
  user_profile: Record<string, any>;
  /** The active run data (mutated in place; echoed back updated). */
  run_data: Record<string, any>;
  /** Op-specific arguments (e.g. chosen rising sign, drilldown center/radius). */
  params?: Record<string, any>;
  /** Storage keys the op may read/write, seeded into the worker's MemoryBackend. */
  storage: StorageSnapshot;
}

export interface WorkerProgress {
  id: number;
  kind: "progress";
  message: string;
  /** 0..1 when known. */
  fraction?: number;
}

export interface WorkerSuccess {
  id: number;
  kind: "success";
  user_profile: Record<string, any>;
  run_data: Record<string, any>;
  /** The op's return value (breakdown table, validation verdict, overview data, …). */
  result: any;
  /** Full post-op storage snapshot; main thread reconciles this against localStorage. */
  storage: StorageSnapshot;
}

export interface WorkerFailure {
  id: number;
  kind: "failure";
  error: string;
  stack?: string;
}

export type WorkerResponse = WorkerProgress | WorkerSuccess | WorkerFailure;

// ---------------------------------------------------------------------------
// Pure snapshot helpers (tested in worker_protocol.test.ts)
// ---------------------------------------------------------------------------

import type { StorageBackend } from "../lib/storage";

/** Serialize the storage keys under any of `prefixes` into a snapshot. */
export function snapshotStorage(backend: StorageBackend, prefixes: string[]): StorageSnapshot {
  const snap: StorageSnapshot = {};
  for (const prefix of prefixes) {
    for (const path of backend.list(prefix)) {
      const value = backend.read(path);
      if (value !== null) snap[path] = value;
    }
  }
  return snap;
}

/** Seed a backend from a snapshot (overwrites matching keys; leaves others untouched). */
export function restoreStorage(backend: StorageBackend, snapshot: StorageSnapshot): void {
  for (const [path, content] of Object.entries(snapshot)) {
    backend.write(path, content);
  }
}

/**
 * Reconcile a post-op snapshot into a backend: writes every key in `next`, and removes keys
 * that existed in `before` (under `prefixes`) but are absent from `next` (the op deleted them,
 * e.g. a full reset clearing candidate times). Returns the paths that changed.
 */
export function reconcileStorage(
  backend: StorageBackend,
  before: StorageSnapshot,
  next: StorageSnapshot,
  prefixes: string[],
): string[] {
  const changed: string[] = [];
  for (const [path, content] of Object.entries(next)) {
    if (backend.read(path) !== content) {
      backend.write(path, content);
      changed.push(path);
    }
  }
  for (const path of Object.keys(before)) {
    if (!(path in next) && prefixes.some((p) => path.startsWith(p))) {
      if (backend.read(path) !== null) {
        backend.remove(path);
        changed.push(path);
      }
    }
  }
  return changed;
}

/** Storage prefixes a worker op may touch for a given user slug. */
export function storagePrefixesForUser(slug: string): string[] {
  // user files (profile/run/times), candidate-time drilldown stores, and shared caches.
  return [`users/${slug}`, "users/_", "openai.json"];
}
